import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  
  // Update document head for SEO
  useEffect(() => {
    document.title = 'Fortal Society: Trading Consapevole, Numeri alla Luce del Sole';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fortal Society: community di trading professionale con trasparenza totale sui risultati. Analisi, formazione, PAMM e copy trading per un trading davvero consapevole.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'fortal society, trading professionale, community trading, risultati verificati, formazione trading, PAMM, copy trading');
    }
    
    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/`;
    
    // Add Schema.org JSON-LD for homepage
    const existingScript = document.querySelector('script[data-schema="homepage"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Fortal Society",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`,
      "logo": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/logo.png`,
      "description": "Community di trading professionale con trasparenza totale sui risultati. Analisi, formazione, PAMM e copy trading per un trading davvero consapevole.",
      "sameAs": [
        "https://t.me/communityfreefortal"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "availableLanguage": "Italian"
      }
    };
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'homepage');
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
    
    return () => {
      const script = document.querySelector('script[data-schema="homepage"]');
      if (script) {
        script.remove();
      }
    };
  }, []);
  
  const services = [
    {
      icon: 'ri-line-chart-line',
      title: 'Analisi',
      description: 'Analisi tecnica e fondamentale approfondita sui mercati finanziari con setup operativi chiari e precisi.'
    },
    {
      icon: 'ri-pulse-line',
      title: 'Operatività',
      description: 'Esecuzione disciplinata delle strategie con risk management rigoroso e journaling completo di ogni trade.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Formazione',
      description: 'Corsi strutturati per sviluppare competenze solide nel trading, dalla teoria alla pratica operativa.'
    }
  ];

  const tradingScreenshots = [
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/42fc0c17df9a205ec56bd2a1d940d0ff.jpeg',
      title: 'Risultati Trading XAUUSD',
      description: 'Performance reale su oro con profitto di +340.33'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/4e3d82fd7cc535b78a44eb5664fead27.jpeg',
      title: 'Performance Trading Avanzata',
      description: 'Analisi dettagliata dei risultati operativi con gestione del rischio'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/2f55a894f49f2ce503b2f5d335e99ca6.jpeg',
      title: 'Operazioni Multi-Asset',
      description: 'Diversificazione strategica su forex, commodities e indici'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/243a9babe1ed98b446c79356a8570d3d.jpeg',
      title: 'Analisi di Mercato Professionale',
      description: 'Setup operativi con indicatori tecnici e gestione avanzata del portafoglio'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/da0fa8932014cb632bc6e8ebebde1ce6.jpeg',
      title: 'Strategia di Trading Vincente',
      description: 'Esecuzione precisa con risk-reward ottimale e disciplina operativa'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/f7a0a4815511ca849c812660294ab00e.jpeg',
      title: 'Trading Mobile Professionale',
      description: 'Gestione operativa avanzata anche in mobilità con risultati consistenti'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/156abe86cfac16aaf4dfdc2ef0b15391.jpeg',
      title: 'Operatività Intraday Avanzata',
      description: 'Scalping e day trading con precisione millimetrica e timing perfetto'
    },
    {
      image: 'https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/156abe86cfac16aaf4dfdc2ef0b15391.jpeg',
      title: 'Trading Professionale Mobile',
      description: 'Gestione avanzata del portafoglio con risultati verificati in tempo reale'
    }
  ];

  // Auto-advance slideshow
  useEffect(() => {
    if (!isModalOpen) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % tradingScreenshots.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [tradingScreenshots.length, isModalOpen]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % tradingScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + tradingScreenshots.length) % tradingScreenshots.length);
  };

  const openModal = (index: number) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextModalImage = () => {
    setModalImageIndex((prev) => (prev + 1) % tradingScreenshots.length);
  };

  const prevModalImage = () => {
    setModalImageIndex((prev) => (prev - 1 + tradingScreenshots.length) % tradingScreenshots.length);
  };

  // Gestione tasti keyboard nel modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isModalOpen) {
        switch (e.key) {
          case 'Escape':
            closeModal();
            break;
          case 'ArrowLeft':
            prevModalImage();
            break;
          case 'ArrowRight':
            nextModalImage();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen]);

  // Crea particelle animate
  useEffect(() => {
    const createParticles = () => {
      const container = document.querySelector('.particles-container');
      if (!container) return;

      for (let i = 0; i < 20; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle animate-particles';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 15 + 's';
        particle.style.animationDuration = (15 + Math.random() * 10) + 's';
        container.appendChild(particle);
      }
    };

    createParticles();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Particelle di sfondo */}
      <div className="particles-container"></div>
      
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://public.readdy.ai/ai/img_res/dcaab8530976c6d66675dadffce0d923.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/20 to-slate-950"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-slide-in-up">
            Fortal Society: trading consapevole,{' '}
            <span className="text-gradient-gold animate-pulse-gold glow-gold-strong">
              numeri alla luce del sole
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto animate-slide-in-up delay-200">
            Analisi, esecuzione, risk management. 
            Trasparenza totale sui risultati per un trading davvero professionale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in-up delay-300">
            <a href="https://t.me/communityfreefortal" target="_blank" rel="noopener noreferrer">
              <Button variant="primary" size="lg" className="animate-pulse-gold hover-lift">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-telegram-line"></i>
                </div>
                Unisciti al Telegram
              </Button>
            </a>
            <Button variant="secondary" size="lg" href="/risultati" className="hover-lift">
              Guarda i Risultati
            </Button>
          </div>
        </div>
      </section>

      {/* Chi Siamo */}
      <section className="py-20 bg-slate-900/30 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Chi Siamo
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                Fortal Society nasce dalla passione per un trading serio e trasparente. 
                Il nostro founder, con anni di esperienza sui mercati, ha sviluppato una metodologia 
                basata su tre pilastri fondamentali.
              </p>
              <div className="space-y-4">
                <div className="flex items-start animate-slide-in-left delay-100">
                  <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                    <i className="ri-shield-check-line text-amber-400 animate-float"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Risk-First Approach</h3>
                    <p className="text-slate-400">La gestione del rischio viene sempre prima del profitto</p>
                  </div>
                </div>
                <div className="flex items-start animate-slide-in-left delay-200">
                  <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                    <i className="ri-settings-3-line text-amber-400 animate-float delay-100"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Processi Strutturati</h3>
                    <p className="text-slate-400">Metodologie testate e ripetibili per ogni operazione</p>
                  </div>
                </div>
                <div className="flex items-start animate-slide-in-left delay-300">
                  <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                    <i className="ri-book-open-line text-amber-400 animate-float delay-200"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Journaling Completo</h3>
                    <p className="text-slate-400">Tracciamento dettagliato di ogni trade per miglioramento continuo</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-in-right">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-yellow-500/20 rounded-2xl blur-xl animate-pulse-gold"></div>
              <img 
                src="https://static.readdy.ai/image/d5376e7f47741c257bcbad68411760a1/829f8b38f3c75438ff21316d8dccb553.jpeg"
                alt="Founder di Fortal Society"
                className="relative rounded-2xl shadow-2xl object-cover w-full h-96 lg:h-[500px] hover-lift glow-gold"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Cosa Facciamo */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Cosa Facciamo
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Il nostro approccio integrato combina analisi approfondite, 
              operatività disciplinata e formazione continua per creare trader consapevoli.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} variant="glass" className="text-center hover-lift animate-bounce-in glass-gradient" style={{animationDelay: `${index * 0.2}s`}}>
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl animate-float glow-gold">
                  <i className={`${service.icon} text-3xl text-amber-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-slate-300">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-slate-900/30 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo Strip */}
          <div className="text-center mb-16 animate-slide-in-up">
            <h3 className="text-lg font-semibold text-slate-400 mb-8">Riconosciuti da</h3>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              <div className="text-2xl font-bold text-slate-500 hover:text-amber-400 transition-colors animate-float">TradingView</div>
              <div className="text-2xl font-bold text-slate-500 hover:text-amber-400 transition-colors animate-float delay-100">MetaTrader</div>
              <div className="text-2xl font-bold text-slate-500 hover:text-amber-400 transition-colors animate-float delay-200">Interactive Brokers</div>
              <div className="text-2xl font-bold text-slate-500 hover:text-amber-400 transition-colors animate-float delay-300">FinTech Awards</div>
            </div>
          </div>

          {/* Trading Screenshots Slideshow */}
          <div className="text-center mb-12 animate-slide-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              I Nostri Risultati in Tempo Reale
            </h2>
            <p className="text-lg text-slate-300">
              Trasparenza totale: ecco le nostre operazioni e performance reali. Clicca per espandere!
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <Card variant="glass" className="overflow-hidden animate-scale-in glass-gradient glow-gold">
              <div className="relative h-96 md:h-[500px]">
                {/* Slideshow Images */}
                <div className="relative w-full h-full overflow-hidden rounded-xl">
                  {tradingScreenshots.map((screenshot, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out cursor-pointer hover:scale-105 ${
                        index === currentSlide 
                          ? 'opacity-100 transform translate-x-0' 
                          : index < currentSlide 
                            ? 'opacity-0 transform -translate-x-full' 
                            : 'opacity-0 transform translate-x-full'
                      }`}
                      onClick={() => openModal(index)}
                    >
                      <img
                        src={screenshot.image}
                        alt={screenshot.title}
                        className="w-full h-full object-cover rounded-xl transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent rounded-xl"></div>
                      
                      {/* Expand Icon */}
                      <div className="absolute top-4 right-4 w-10 h-10 bg-slate-900/80 backdrop-blur-sm rounded-full flex items-center justify-center text-amber-400 hover:bg-amber-400/20 transition-all duration-300 opacity-80 hover:opacity-100">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-fullscreen-line"></i>
                        </div>
                      </div>

                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                          {screenshot.title}
                        </h3>
                        <p className="text-slate-300">
                          {screenshot.description}
                        </p>
                        <p className="text-amber-400 text-sm mt-2 flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-cursor-line"></i>
                          </div>
                          Clicca per ingrandire
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-900/80 hover:bg-amber-400/20 rounded-full flex items-center justify-center text-white hover:text-amber-400 transition-all duration-300 backdrop-blur-sm border border-amber-400/20 hover:border-amber-400/40 hover-lift"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xl"></i>
                  </div>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-900/80 hover:bg-amber-400/20 rounded-full flex items-center justify-center text-white hover:text-amber-400 transition-all duration-300 backdrop-blur-sm border border-amber-400/20 hover:border-amber-400/40 hover-lift"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xl"></i>
                  </div>
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {tradingScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-amber-400 scale-125 glow-gold'
                          : 'bg-slate-600 hover:bg-slate-500'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Modal per immagini espanse */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          {/* Overlay per chiudere */}
          <div 
            className="absolute inset-0 cursor-pointer" 
            onClick={closeModal}
          ></div>
          
          {/* Contenuto Modal */}
          <div className="relative max-w-7xl max-h-[90vh] mx-4 bg-slate-900/95 backdrop-blur-md rounded-2xl border border-amber-400/20 overflow-hidden">
            {/* Header Modal */}
            <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-slate-900/90 to-transparent">
              <div className="flex justify-between items-center">
                <div className="text-amber-400 font-medium">
                  {modalImageIndex + 1} / {tradingScreenshots.length}
                </div>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 bg-slate-800/80 hover:bg-red-500/20 rounded-full flex items-center justify-center text-slate-300 hover:text-red-400 transition-all duration-300 backdrop-blur-sm border border-slate-700 hover:border-red-400/40"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>
            </div>

            {/* Immagine principale */}
            <div className="relative">
              <img
                src={tradingScreenshots[modalImageIndex].image}
                alt={tradingScreenshots[modalImageIndex].title}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-900/95 via-slate-900/80 to-transparent">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {tradingScreenshots[modalImageIndex].title}
                </h3>
                <p className="text-slate-300 text-lg">
                  {tradingScreenshots[modalImageIndex].description}
                </p>
              </div>
            </div>

            {/* Controlli navigazione */}
            <button
              onClick={prevModalImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-800/80 hover:bg-amber-400/20 rounded-full flex items-center justify-center text-white hover:text-amber-400 transition-all duration-300 backdrop-blur-sm border border-amber-400/20 hover:border-amber-400/40"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl"></i>
              </div>
            </button>

            <button
              onClick={nextModalImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-slate-800/80 hover:bg-amber-400/20 rounded-full flex items-center justify-center text-white hover:text-amber-400 transition-all duration-300 backdrop-blur-sm border border-amber-400/20 hover:border-amber-400/40"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xl"></i>
              </div>
            </button>

            {/* Thumbnails navigation */}
            <div className="p-4 bg-slate-900/95">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {tradingScreenshots.map((screenshot, index) => (
                  <button
                    key={index}
                    onClick={() => setModalImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      index === modalImageIndex
                        ? 'border-amber-400 glow-gold scale-110'
                        : 'border-slate-600 hover:border-amber-400/50 hover:scale-105'
                    }`}
                  >
                    <img
                      src={screenshot.image}
                      alt={screenshot.title}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Istruzioni keyboard */}
            <div className="absolute bottom-4 left-4 text-slate-400 text-sm">
              <div className="flex items-center gap-4 opacity-75">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">ESC</kbd>
                  Chiudi
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">←→</kbd>
                  Naviga
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Final */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card variant="glass" className="text-center animate-bounce-in glass-gradient glow-gold-strong">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto a Fare Trading Seriamente?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Unisciti alla community di trader che hanno scelto la trasparenza e la professionalità. 
              Accesso immediato alle analisi, ai risultati e al supporto della community.
            </p>
            <a href="https://t.me/communityfreefortal" target="_blank" rel="noopener noreferrer">
              <Button variant="primary" size="lg" className="animate-pulse-gold hover-lift glow-gold">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-telegram-line"></i>
                </div>
                Apri Telegram Ora
              </Button>
            </a>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}